import React, { useEffect, useState, useMemo } from "react";
import api from "../../../../api/axios";
import GlobalLibraryToolbar from "../Toolbars/GlobalLibraryToolbar";
import GlobalLibraryCatalog from "../Catalog/GlobalLibraryCatalog";
import ArticleTreeView from "../TreeView/ArticleTreeView";
import ArticleModal from "../Toolbars/ArticleModal";
import ArticleDetails from "../Details/ArticleDetails";
import ArticleImageGallery from "../../../Common/ArticleImageGallery";
import ArticleExcelImporter from "../ExcelExportImport/ArticleExcelImporter";
import { exportArticlesToExcel } from "../ExcelExportImport/ArticleExcelIO";
import { useCurrentTenant } from "../../../../hooks/useCurrentTenant";
import "./GlobalLibrary.css";

const OWNER_TENANT = "Innovation_Projects";

const GlobalLibrary = () => {
    const currentTenantId = useCurrentTenant();
    const isOwner = currentTenantId === OWNER_TENANT;

    const [view, setView] = useState("catalog");
    const [groupBy, setGroupBy] = useState("manufacturer");
    const [search, setSearch] = useState("");
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [checkedIds, setCheckedIds] = useState(new Set());
    const [globalArticles, setGlobalArticles] = useState([]);
    const [tenantArticles, setTenantArticles] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingArticle, setEditingArticle] = useState(null);

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTenantId]);

    useEffect(() => {
        setCheckedIds(new Set());
        setSelectedArticle(null);
    }, [currentTenantId]);

    const fetchAll = async () => {
        try {
            const [subGroups, enclosures, ducts, rails] = await Promise.all([
                api.get("/subgroup"),
                api.get("/enclosure"),
                api.get("/duct"),
                api.get("/rail"),
            ]);

            const all = [...subGroups.data, ...enclosures.data, ...ducts.data, ...rails.data];

            setGlobalArticles(all.filter((a) => a.ownership === "Global"));
            setTenantArticles(all.filter((a) => a.tenantId === currentTenantId));
            setSelectedArticle((prev) =>
                prev ? all.find((a) => a.id === prev.id) ?? null : prev
            );
        } catch (err) {
            console.error(err);
        }
    };

    const importedSourceIds = useMemo(
        () =>
            new Set(
                tenantArticles.filter((a) => a.sourceArticleId).map((a) => a.sourceArticleId)
            ),
        [tenantArticles]
    );

    const isImported = (article) => importedSourceIds.has(article.id);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return globalArticles.filter(
            (a) =>
                a.name?.toLowerCase().includes(q) ||
                a.manufacturer?.toLowerCase().includes(q) ||
                a.type?.toLowerCase().includes(q) ||
                a.articleNumber?.toLowerCase().includes(q)
        );
    }, [globalArticles, search]);

    const canCheck = (article) => {
        if (isOwner) return true;
        return !isImported(article);
    };

    // --- Import (non-owner tenants) ---
    const importArticles = async (sourceArticleIds) => {
        const { data } = await api.post(
            "/article/import",
            { sourceArticleIds },
            { headers: { "X-Tenant": currentTenantId } }
        );
        return data; // { imported: [...], failed: [{ sourceArticleId, reason }] }
    };

    const handleImportOne = async (article) => {
        try {
            const result = await importArticles([article.id]);
            if (result.failed.length > 0) {
                console.error("Import failed:", result.failed);
            }
            fetchAll();
        } catch (err) {
            console.error("Import failed", err);
        }
    };

    const handleImportSelected = async () => {
        if (checkedIds.size === 0) return;

        const toImport = globalArticles
            .filter((a) => checkedIds.has(a.id) && !isImported(a))
            .map((a) => a.id);

        try {
            const result = await importArticles(toImport);
            if (result.failed.length > 0) {
                console.error(
                    `${result.failed.length} of ${toImport.length} imports failed`,
                    result.failed
                );
            }
        } catch (err) {
            console.error("Bulk import failed", err);
        }

        setCheckedIds(new Set());
        fetchAll();
    };

    // --- Owner-only management ---
    const deleteOne = async (article) => {
        switch (article.partType) {
            case "SubGroup":
                return api.delete(`/subgroup/${article.id}`);
            case "Enclosure":
                return api.delete(`/enclosure/${article.id}`);
            case "Rail":
                return api.delete(`/rail/${article.id}`);
            case "Duct":
                return api.delete(`/duct/${article.id}`);
            default:
                throw new Error(`Unsupported type for article ${article.id}`);
        }
    };

    const handleBulkDelete = async () => {
        if (!isOwner || checkedIds.size === 0) return;

        const toDelete = globalArticles.filter((a) => checkedIds.has(a.id));
        const confirmed = window.confirm(`Delete ${toDelete.length} global article(s)?`);
        if (!confirmed) return;

        const results = await Promise.allSettled(toDelete.map((a) => deleteOne(a)));
        const failed = results.filter((r) => r.status === "rejected");
        if (failed.length > 0) {
            console.error(`${failed.length} of ${toDelete.length} deletes failed`, failed);
        }

        setCheckedIds(new Set());
        if (selectedArticle && checkedIds.has(selectedArticle.id)) setSelectedArticle(null);
        fetchAll();
    };

    const handleAdd = () => {
        if (!isOwner) return;
        setEditingArticle(null);
        setModalOpen(true);
    };

    const handleImport = () => {
        if (!isOwner) return; // only Innovation_Projects adds to the global library
        document.getElementById("global-excel-importer-trigger").click();
    };

    const handleExport = () => {
        exportArticlesToExcel(filtered, "global_library");
    };

    const handleSave = async (article) => {
        try {
            const payload = { ...article, ownership: "Global" };
            const isUpdate = !!payload.id;
            let response;

            if (isUpdate) {
                switch (payload.partType) {
                    case "SubGroup":
                        response = await api.put(`/subgroup/${payload.id}`, payload);
                        break;
                    case "Enclosure":
                        response = await api.put(`/enclosure/${payload.id}`, payload);
                        break;
                    case "Rail":
                        response = await api.put(`/rail/${payload.id}`, payload);
                        break;
                    case "Duct":
                        response = await api.put(`/duct/${payload.id}`, payload);
                        break;
                    default:
                        throw new Error("Unsupported part type");
                }
            } else {
                switch (payload.partType) {
                    case "SubGroup":
                        response = await api.post("/subgroup", payload);
                        break;
                    case "Enclosure":
                        response = await api.post("/enclosure", payload);
                        break;
                    case "Rail":
                        response = await api.post("/rail", payload);
                        break;
                    case "Duct":
                        response = await api.post("/duct", payload);
                        break;
                    default:
                        throw new Error("Unsupported part type");
                }
            }

            fetchAll();
            return response?.data;

        } catch (err) {
            console.error("Failed to save global article", err);
            throw err;
        }
    };

    const handleDeleteOne = async (article) => {
        if (!isOwner) return;
        const confirmed = window.confirm(`Delete article "${article.name}"?`);
        if (!confirmed) return;

        try {
            await deleteOne(article);
            setSelectedArticle(null);
            fetchAll();
        } catch (err) {
            console.error(err);
        }
    };

    const renderBadge = (article) =>
        isImported(article) ? <span className="badge badge-imported" /> : null;

    const selectableFiltered = useMemo(
        () => filtered.filter((a) => canCheck(a)),
        [filtered, isOwner, importedSourceIds] // canCheck depends on isOwner/isImported
    );

    const allSelected =
        selectableFiltered.length > 0 &&
        selectableFiltered.every((a) => checkedIds.has(a.id));

    const handleToggleSelectAll = (checked) => {
        if (checked) {
            setCheckedIds(new Set(selectableFiltered.map((a) => a.id)));
        } else {
            setCheckedIds(new Set());
        }
    };

    return (
        <div>
            <GlobalLibraryToolbar
                search={search}
                setSearch={setSearch}
                groupBy={groupBy}
                setGroupBy={setGroupBy}
                view={view}
                setView={setView}
                isOwner={isOwner}
                selectedCount={checkedIds.size}
                totalSelectableCount={selectableFiltered.length}
                allSelected={allSelected}
                onToggleSelectAll={handleToggleSelectAll}
                onAdd={handleAdd}
                onImport={handleImport}
                onExport={handleExport}
                onDeleteSelected={handleBulkDelete}
                onImportSelected={handleImportSelected}
            />

            <p>Global articles: {filtered.length}</p>

            {isOwner && (
                <ArticleModal
                    isOpen={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setEditingArticle(null);
                    }}
                    onSave={handleSave}
                    editingArticle={editingArticle}
                />
            )}

            {isOwner && (
                <ArticleExcelImporter
                    inputId="global-excel-importer-trigger"
                    ownership="Global"
                    tenantId={currentTenantId}
                    defaultPartType="SubGroup"
                    onImportComplete={fetchAll}
                />
            )}

            <div className={`gl-layout ${view === "list" ? "gl-layout-list" : "gl-layout-catalog"}`}>
                {view === "catalog" ? (
                    <div className="gl-catalog-pane">
                        <GlobalLibraryCatalog
                            articles={filtered}
                            selectedArticle={selectedArticle}
                            onSelect={setSelectedArticle}
                            checkedIds={checkedIds}
                            onCheckedIdsChange={setCheckedIds}
                            canCheck={canCheck}
                            isImported={isImported}
                            onImportOne={handleImportOne}
                            isOwner={isOwner}
                        />
                    </div>
                ) : (
                    <div className="gl-list-pane">
                        <ArticleTreeView
                            articles={filtered}
                            groupBy={groupBy}
                            search=""
                            selectedArticle={selectedArticle}
                            onSelect={setSelectedArticle}
                            checkedIds={checkedIds}
                            onCheckedIdsChange={setCheckedIds}
                            canCheck={canCheck}
                            checkboxDisabledTitle={isOwner ? "Not available" : "Already imported"}
                            renderBadge={renderBadge}
                        />
                    </div>
                )}
                <div className="gl-details-pane">
                    <ArticleDetails
                        article={selectedArticle}
                        onEdit={
                            isOwner
                                ? (article) => {
                                    setEditingArticle(article);
                                    setModalOpen(true);
                                }
                                : undefined
                        }
                        onDelete={isOwner ? handleDeleteOne : undefined}
                        onImport={!isOwner ? handleImportOne : undefined}
                        isImported={selectedArticle ? isImported(selectedArticle) : false}
                    />

                    <ArticleImageGallery article={selectedArticle} variant="large" />
                </div>
            </div>
        </div>
    );
};

export default GlobalLibrary;